import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  buildFulfillmentAcceptance,
  mapFulfillmentError,
  useAuthSession,
  useLocalities,
  usePreparedFulfillmentQuote,
  useStoreFulfillmentSettings,
  useTranslation,
  type AdminOrderSellableVariant,
  type FulfillmentQuote,
  type FulfillmentSelection,
  type PublicFulfillmentOptions,
  type TranslationKey,
} from '@dukanti/shared';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { useCreateAdminPreparedOrder } from '@/hooks/use-create-admin-prepared-order';
import {
  buildCreateAdminPreparedOrderInput,
  getPreparedOrderBlockingErrors,
  makeDefaultPreparedOrderDeliveryDraft,
  mergePreparedOrderLine,
  removePreparedOrderLine,
  updatePreparedOrderLineQuantity,
  validatePreparedOrderLines,
  type PreparedOrderDeliveryDraft,
  type PreparedOrderLine,
} from '../lib/admin-prepared-order-schema';
import { AdminPreparedOrderDeliveryForm } from './admin-prepared-order-delivery-form';
import { AdminPreparedOrderLines } from './admin-prepared-order-lines';
import { AdminPreparedOrderReview } from './admin-prepared-order-review';
import { AdminPreparedOrderSellablePicker } from './admin-prepared-order-sellable-picker';

type PrepareStep = 'items' | 'delivery' | 'review';
export interface AdminPreparedOrderFormProps {
  readonly storeId: string;
}

export function AdminPreparedOrderForm(props: AdminPreparedOrderFormProps): React.JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const session = useAuthSession(supabase);
  const localities = useLocalities(supabase);
  const settings = useStoreFulfillmentSettings(supabase, props.storeId);
  const createOrder = useCreateAdminPreparedOrder(props.storeId);
  const [step, setStep] = useState<PrepareStep>('items');
  const [searchQuery, setSearchQuery] = useState('');
  const [lines, setLines] = useState<PreparedOrderLine[]>([]);
  const [delivery, setDelivery] = useState<PreparedOrderDeliveryDraft>(
    makeDefaultPreparedOrderDeliveryDraft,
  );
  const [selection, setSelection] = useState<FulfillmentSelection | null>(null);
  const [errorKey, setErrorKey] = useState<TranslationKey | null>(null);
  const [showDeliveryValidation, setShowDeliveryValidation] = useState(false);
  const [review, setReview] = useState<{
    readonly key: string;
    readonly quote: FulfillmentQuote;
  } | null>(null);
  const [isReviewing, setIsReviewing] = useState(false);
  const options = useMemo<PublicFulfillmentOptions | null>(
    () =>
      settings.data
        ? {
            storeId: settings.data.storeId,
            revision: settings.data.revision,
            currency: settings.data.currency,
            delivery:
              settings.data.deliveryFeeCents === null
                ? null
                : { feeCents: settings.data.deliveryFeeCents },
            pickupPlaces: settings.data.pickupPlaces,
          }
        : null,
    [settings.data],
  );
  useEffect(() => {
    if (selection || !options) return;
    if (options.delivery && options.pickupPlaces.length === 0)
      setSelection({ method: 'delivery', pickupPlaceId: null });
    else if (!options.delivery && options.pickupPlaces.length === 1 && options.pickupPlaces[0])
      setSelection({ method: 'pickup', pickupPlaceId: options.pickupPlaces[0].id });
  }, [selection, options]);
  const selectionAvailable =
    !settings.isError &&
    !!options &&
    !!selection &&
    (selection.method === 'delivery'
      ? options.delivery !== null
      : options.pickupPlaces.some((place) => place.id === selection.pickupPlaceId));
  const lineError = validatePreparedOrderLines(lines);
  const quoteInput = useMemo(
    () =>
      selectionAvailable && selection && !lineError
        ? {
            storeId: props.storeId,
            selection,
            lines: lines.map((line) => ({
              variant_id: line.variant.variant_id,
              quantity: line.quantity,
            })),
          }
        : null,
    [selectionAvailable, selection, lineError, props.storeId, lines],
  );
  const quote = usePreparedFulfillmentQuote(supabase, session.session?.user.id ?? null, quoteInput);
  // A response arriving after any method, contact or line edit cannot authorize the new draft.
  const draftKey = JSON.stringify([
    props.storeId,
    session.session?.user.id,
    quoteInput,
    delivery,
    options?.revision,
  ]);
  const currentDraftKey = useRef(draftKey);
  currentDraftKey.current = draftKey;
  const requestGeneration = useRef(0);
  const reviewedQuote = review?.key === draftKey && selectionAvailable ? review.quote : null;
  const selectedVariantIds = useMemo(
    () => new Set(lines.map((line) => line.variant.variant_id)),
    [lines],
  );

  useEffect(() => {
    if (step !== 'delivery' || !showDeliveryValidation || !errorKey) return;
    const fieldId =
      errorKey === 'admin.orders.prepare.errors.phoneRequired' ||
      errorKey === 'admin.orders.prepare.errors.phoneInvalid'
        ? 'prepared-order-phone-number'
        : errorKey === 'admin.orders.prepare.errors.localityRequired' ||
            errorKey === 'admin.orders.prepare.errors.localityInvalid'
          ? 'prepared-order-locality-search'
          : null;
    if (fieldId) document.getElementById(fieldId)?.focus();
  }, [step, showDeliveryValidation, errorKey]);

  async function reviewDraft(): Promise<void> {
    if (createOrder.isPending || isReviewing) return;
    setShowDeliveryValidation(true);
    setReview(null);
    const blocking = getPreparedOrderBlockingErrors({
      lines,
      delivery,
      localities: localities.data ?? [],
      fulfillment: selection ?? undefined,
    });
    if (!selectionAvailable || !quoteInput) {
      setErrorKey(lineError ?? 'fulfillment.unavailable');
      if (!lineError) setStep('delivery');
      return;
    }
    if (blocking[0]) {
      setErrorKey(blocking[0]);
      setStep('delivery');
      return;
    }
    const requestedKey = draftKey;
    const generation = ++requestGeneration.current;
    setIsReviewing(true);
    setErrorKey(null);
    try {
      const refreshed = await quote.refetch();
      if (generation !== requestGeneration.current || currentDraftKey.current !== requestedKey)
        return;
      if (refreshed.isError || !refreshed.data) {
        setErrorKey('fulfillment.quoteFailed');
        return;
      }
      if (!refreshed.data.success) {
        setErrorKey(mapFulfillmentError(refreshed.data.error_code) ?? 'fulfillment.quoteFailed');
        return;
      }
      setReview({ key: requestedKey, quote: refreshed.data.value });
      setStep('review');
    } finally {
      if (generation === requestGeneration.current) setIsReviewing(false);
    }
  }
  function changeStep(next: PrepareStep): void {
    if (createOrder.isPending || isReviewing) return;
    setReview(null);
    setErrorKey(null);
    if (next === 'review') void reviewDraft();
    else setStep(next);
  }
  function handleCreate(): void {
    if (!reviewedQuote || !selection || createOrder.isPending || isReviewing) return;
    const built = buildCreateAdminPreparedOrderInput({
      storeId: props.storeId,
      lines,
      delivery,
      localities: localities.data ?? [],
      fulfillment: selection,
      acceptance: buildFulfillmentAcceptance(reviewedQuote),
    });
    if (!built.ok) {
      setErrorKey(built.error);
      return;
    }
    setErrorKey(null);
    createOrder.mutate(built.input, {
      onSuccess: (result) => {
        if (!result.success || !result.order_id) {
          setReview(null);
          setErrorKey(mapFulfillmentError(result.error_code) ?? 'fulfillment.quoteFailed');
          void settings.refetch();
          return;
        }
        toast.success(t('admin.orders.prepare.success.created'));
        navigate(`/orders/${result.order_id}`);
      },
      onError: () => {
        setReview(null);
        setErrorKey('fulfillment.quoteFailed');
      },
    });
  }
  return (
    <div className="space-y-5">
      <div className="border-border bg-card shadow-admin-card flex flex-wrap gap-2 rounded-xl border p-2">
        {(['items', 'delivery', 'review'] as const).map((candidate) => (
          <Button
            key={candidate}
            type="button"
            variant={step === candidate ? 'default' : 'ghost'}
            disabled={createOrder.isPending || isReviewing}
            onClick={() => changeStep(candidate)}
          >
            {t(
              candidate === 'delivery'
                ? 'fulfillment.title'
                : `admin.orders.prepare.steps.${candidate}`,
            )}
          </Button>
        ))}
      </div>
      {settings.isError ? (
        <div role="alert" className="text-destructive text-sm">
          <p>{t('fulfillment.loadFailed')}</p>
          <Button type="button" variant="outline" onClick={() => void settings.refetch()}>
            {t('common.retry')}
          </Button>
        </div>
      ) : null}
      {settings.isLoading ? <p role="status">{t('common.loading')}</p> : null}
      {isReviewing ? (
        <p role="status" aria-live="polite">
          {t('fulfillment.quoteLoading')}
        </p>
      ) : null}
      {errorKey && step !== 'review' ? (
        <p role="alert" className="text-destructive text-sm">
          {t(errorKey)}
        </p>
      ) : null}
      {step === 'items' ? (
        <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_420px]">
          <AdminPreparedOrderSellablePicker
            storeId={props.storeId}
            query={searchQuery}
            onQueryChange={setSearchQuery}
            selectedVariantIds={selectedVariantIds}
            onAdd={(variant: AdminOrderSellableVariant) => {
              setLines((current) => mergePreparedOrderLine(current, variant));
              setReview(null);
              setErrorKey(null);
            }}
          />
          <AdminPreparedOrderLines
            lines={lines}
            onQuantityChange={(variantId, quantity) => {
              setLines((current) => updatePreparedOrderLineQuantity(current, variantId, quantity));
              setReview(null);
            }}
            onRemove={(variantId) => {
              setLines((current) => removePreparedOrderLine(current, variantId));
              setReview(null);
            }}
          />
        </div>
      ) : null}
      {step === 'delivery' ? (
        <AdminPreparedOrderDeliveryForm
          value={delivery}
          onChange={(value) => {
            setDelivery(value);
            setReview(null);
            setErrorKey(null);
          }}
          showValidation={showDeliveryValidation}
          fulfillment={selection}
          options={options}
          onFulfillmentChange={(value) => {
            setSelection(value);
            setReview(null);
            setErrorKey(null);
          }}
        />
      ) : null}
      {step === 'review' ? (
        <AdminPreparedOrderReview
          lines={lines}
          delivery={delivery}
          localities={localities.data ?? []}
          fulfillment={selection}
          quote={reviewedQuote}
          isPending={createOrder.isPending || isReviewing}
          errorKey={errorKey}
          onEditItems={() => changeStep('items')}
          onEditDelivery={() => changeStep('delivery')}
          onCreate={handleCreate}
          onRefresh={() => void reviewDraft()}
        />
      ) : null}
    </div>
  );
}
